import {
  InfiniteData,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { useUserRole } from '@/hooks/useUserRole';
import {
  createClientGroup,
  createDeal,
  createListing,
  createMember,
  deleteClientGroup,
  deleteDeal,
  deleteListing,
  deleteMember,
  getMyClientGroups,
  getMyClientGroupsPaginated,
  getMyGroupMemberships,
  setGroupInboxState,
  updateClientGroup,
  updateDeal,
  updateListing,
  updateMember,
} from '@/services/api/clientGroups';
import {
  ClientGroup,
  CreateDealRequest,
  CreateGroupRequest,
  CreateListingRequest,
  CreateMemberRequest,
  DealListing,
  UpdateDealRequest,
  UpdateGroupRequest,
  UpdateListingRequest,
  UpdateMemberRequest,
} from '@/types/clientGroups';

export const CLIENT_GROUPS_QUERY_KEY = ['client', 'groups'];

type ClientGroupsPage = { groups: ClientGroup[]; nextCursor?: string };
type ClientGroupsCache = InfiniteData<ClientGroupsPage, string | undefined>;

/**
 * Update every cached client-group page without discarding pagination
 * metadata. All query variants under CLIENT_GROUPS_QUERY_KEY use
 * useInfiniteQuery, so optimistic mutations must operate on pages rather
 * than the pre-pagination `{ groups }` cache shape.
 */
const updateCachedClientGroups = (
  queryClient: QueryClient,
  updateGroup: (group: ClientGroup) => ClientGroup,
): Array<[QueryKey, ClientGroupsCache | undefined]> => {
  const previousQueries = queryClient.getQueriesData<ClientGroupsCache>({
    queryKey: CLIENT_GROUPS_QUERY_KEY,
  });

  previousQueries.forEach(([queryKey, previousData]) => {
    if (!previousData) return;
    queryClient.setQueryData<ClientGroupsCache>(queryKey, {
      ...previousData,
      pages: previousData.pages.map((page) => ({
        ...page,
        groups: page.groups.map(updateGroup),
      })),
    });
  });

  return previousQueries;
};

const restoreClientGroupQueries = (
  queryClient: QueryClient,
  previousQueries: Array<[QueryKey, ClientGroupsCache | undefined]> | undefined,
) => {
  previousQueries?.forEach(([queryKey, previousData]) => {
    if (previousData) queryClient.setQueryData(queryKey, previousData);
  });
};

// ─── Query hook ────────────────────────────────────────────────────────────────

/**
 * Fetch all client groups for the current user.
 * Advisors, admins, and super-admins get groups they own; buyers get groups they are members of.
 * Admins/super-admins may pass an advisorId to view a specific advisor's groups.
 *
 * The admin "all advisors" case is server-paginated (see #1268); this hook
 * pages through every page in the background so its many consumers keep
 * seeing the full flattened list, unchanged, same pattern as
 * useAdminProperties. Other cases (a specific advisorId, or a buyer's own
 * memberships) return `nextCursor: undefined` from the backend, so this is a
 * harmless single fetch for them, same as before pagination existed.
 */
export const useClientGroups = (advisorId?: string) => {
  const { isAdvisor, isAdmin } = useUserRole();
  const isAdvisorOrAbove = isAdvisor || isAdmin; // isAdmin includes SuperAdmin
  const queryKey = advisorId ? [...CLIENT_GROUPS_QUERY_KEY, advisorId] : CLIENT_GROUPS_QUERY_KEY;

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      isAdvisorOrAbove ? getMyClientGroups(advisorId, pageParam) : getMyGroupMemberships(),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  return {
    groups: query.data?.pages.flatMap((page) => page.groups) ?? [],
    isLoading: query.isLoading,
    // Only "fetching" in the consumer-visible sense while the first page is
    // in flight or a manual refetch is running — background page-2-onward
    // fetches shouldn't flip every consumer's loading spinner back on.
    isFetching: query.isFetching && !query.isFetchingNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Admin "all advisors" client groups list, real single-page-at-a-time
 * pagination + server-side search on holdingName (see #1268 — this replaces
 * the old getAllClientGroups() call that scanned + fully nested-enriched
 * every group in the system on every load). Search term is part of the
 * query key, so changing it resets pagination for free.
 *
 * Member-name/email and deal-name/status matching stay a client-side filter
 * over whatever page(s) are loaded — those live on separate tables not
 * present on the scanned ClientGroup item, so the server-side search here
 * only covers holdingName. See ClientGroupDBClient.searchClientGroups's own
 * comment for the full rationale.
 */
export const useAdminClientGroupsSearch = ({
  search = '',
  enabled = true,
}: { search?: string; enabled?: boolean } = {}) => {
  const query = useInfiniteQuery({
    queryKey: [...CLIENT_GROUPS_QUERY_KEY, 'admin-search', search],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      getMyClientGroupsPaginated({ cursor: pageParam, search: search || undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 1000,
    enabled,
  });

  return {
    groups: query.data?.pages.flatMap((page) => page.groups) ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};

// ─── Group mutation hooks ──────────────────────────────────────────────────────

export const useCreateClientGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGroupRequest) => createClientGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useUpdateClientGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGroupRequest }) =>
      updateClientGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useDeleteClientGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteClientGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useSetGroupInboxState = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, isInboxArchived }: { groupId: string; isInboxArchived: boolean }) =>
      setGroupInboxState(groupId, isInboxArchived),
    onMutate: async ({ groupId, isInboxArchived }) => {
      await queryClient.cancelQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      const previousQueries = updateCachedClientGroups(queryClient, (group) =>
        group.id === groupId ? { ...group, isInboxArchived } : group,
      );

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      restoreClientGroupQueries(queryClient, context?.previousQueries);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

// ─── Deal mutation hooks ───────────────────────────────────────────────────────

export const useCreateDeal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: CreateDealRequest }) =>
      createDeal(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useUpdateDeal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealId, data }: { dealId: string; data: UpdateDealRequest }) =>
      updateDeal(dealId, data),
    // Optimistic update for instant kanban feel — updates all cached variants (base + admin-scoped)
    onMutate: async ({ dealId, data }) => {
      await queryClient.cancelQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      const previousQueries = updateCachedClientGroups(queryClient, (group) => ({
        ...group,
        deals: group.deals.map((deal) => {
          if (deal.id !== dealId) return deal;
          const updated = { ...deal, ...data };
          if (data.feeType === 'Percentage') delete updated.feeOverride;
          if (data.feeType === 'Flat') delete updated.commissionRate;
          return updated;
        }),
      }));

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      restoreClientGroupQueries(queryClient, context?.previousQueries);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useDeleteDeal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dealId: string) => deleteDeal(dealId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

// ─── Listing mutation hooks ────────────────────────────────────────────────────

export const useCreateListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealId, data }: { dealId: string; data: CreateListingRequest }) =>
      createListing(dealId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useUpdateListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listingId, data }: { listingId: string; data: UpdateListingRequest }) =>
      updateListing(listingId, data),
    // Optimistic update for instant kanban drag-and-drop — updates all cached variants (base + admin-scoped)
    onMutate: async ({ listingId, data }) => {
      await queryClient.cancelQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      const previousQueries = updateCachedClientGroups(queryClient, (group) => ({
        ...group,
        deals: group.deals.map((deal) => ({
          ...deal,
          listings: (deal.listings ?? []).map(
            (listing): DealListing =>
              listing.id === listingId ? { ...listing, ...data } : listing,
          ),
        })),
      }));

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      restoreClientGroupQueries(queryClient, context?.previousQueries);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      if (variables.data.status === 'Passed' || variables.data.status === 'Lost') {
        queryClient.invalidateQueries({ queryKey: ['properties'] });
      }
    },
  });
};

export const useDeleteListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (listingId: string) => deleteListing(listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

// ─── Member mutation hooks ─────────────────────────────────────────────────────

export const useCreateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: CreateMemberRequest }) =>
      createMember(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useUpdateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, data }: { memberId: string; data: UpdateMemberRequest }) =>
      updateMember(memberId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};

export const useDeleteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: string) => deleteMember(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
    },
  });
};
